import React, { createContext, useContext, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getCurrentUser, setCurrentUser } from "@/api";
import {
  SUPPORTED_LANGUAGES,
  cacheLanguageForUser,
  getUiPhraseTranslationMap,
  resolveCachedLanguageForUser,
} from "@/i18n";

const NON_TRANSLATABLE_PARENT_SELECTOR = [
  "[data-i18n-exclude='true']",
  ".mmj-pdf",
  "#mmj-print-root",
  "#mmj-pt-print-root",
  "#mmj-repair-print-root",
  ".invoice-print",
  ".print-hidden",
].join(",");

const LanguageContext = createContext({
  language: "en",
  changeLanguage: async () => {},
  supportedLanguages: SUPPORTED_LANGUAGES,
});

function normalizeLanguageCode(input) {
  const raw = String(input || "en").trim().toLowerCase();
  const base = raw.split("-")[0].split("_")[0];
  if (SUPPORTED_LANGUAGES.some((x) => x.code === raw)) return raw;
  return SUPPORTED_LANGUAGES.some((x) => x.code === base) ? base : "en";
}

function translateNodeTree(root, phraseMap) {
  if (!root || !phraseMap || phraseMap.size === 0) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const parent = current.parentElement;
    const parentTag = String(parent?.tagName || "").toUpperCase();
    const skipByTag = parentTag === "TD" || parentTag === "CODE" || parentTag === "PRE";
    if (parent && !skipByTag && !parent.closest(NON_TRANSLATABLE_PARENT_SELECTOR)) {
      const raw = String(current.nodeValue || "");
      const trimmed = raw.trim();
      if (trimmed && phraseMap.has(trimmed)) {
        const translated = phraseMap.get(trimmed);
        if (translated && translated !== trimmed) {
          current.nodeValue = raw.replace(trimmed, translated);
        }
      }
    }
    current = walker.nextNode();
  }
}

export function LanguageProvider({ children }) {
  const { i18n: i18nInstance } = useTranslation();
  const applyUserPreferredLanguage = useCallback(
    async (userLike) => {
      const userId = String(userLike?.id || "").trim();
      const preferred = normalizeLanguageCode(
        resolveCachedLanguageForUser(userId) || userLike?.preferred_language || "en"
      );
      if (preferred !== normalizeLanguageCode(i18nInstance.language)) {
        await i18nInstance.changeLanguage(preferred);
      }
      document.documentElement.lang = preferred;
      cacheLanguageForUser(userId, preferred);
    },
    [i18nInstance]
  );

  const changeLanguage = useCallback(
    async (langCode) => {
      const code = normalizeLanguageCode(langCode);
      await i18nInstance.changeLanguage(code);
      document.documentElement.lang = code;
      const currentUser = getCurrentUser();
      const userId = String(currentUser?.id || "").trim();
      cacheLanguageForUser(userId, code);
      if (currentUser && typeof currentUser === "object") {
        const nextUser = { ...currentUser, preferred_language: code };
        setCurrentUser(nextUser);
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("mmj-user-context-updated", {
              detail: { user: nextUser },
            })
          );
        }
      }
      // Keep language preference local-first to avoid noisy 404s on backends
      // that do not expose /auth/update-language in this runtime mode.
    },
    [i18nInstance]
  );

  useEffect(() => {
    applyUserPreferredLanguage(getCurrentUser()).catch(() => {});
    const onUserContext = (event) => {
      applyUserPreferredLanguage(event?.detail?.user || getCurrentUser()).catch(() => {});
    };
    window.addEventListener("mmj-user-context-updated", onUserContext);
    return () => window.removeEventListener("mmj-user-context-updated", onUserContext);
  }, [applyUserPreferredLanguage]);

  useEffect(() => {
    const code = normalizeLanguageCode(i18nInstance.language || "en");
    if (code === "en") return undefined;

    const phraseMap = getUiPhraseTranslationMap(code);
    const root = document.getElementById("mmj-ui-root") || document.body;
    if (!root) return undefined;

    translateNodeTree(root, phraseMap);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const parent = node.parentElement;
            const parentTag = String(parent?.tagName || "").toUpperCase();
            const skipByTag = parentTag === "TD" || parentTag === "CODE" || parentTag === "PRE";
            if (parent && !skipByTag && !parent.closest(NON_TRANSLATABLE_PARENT_SELECTOR)) {
              const raw = String(node.nodeValue || "");
              const trimmed = raw.trim();
              if (trimmed && phraseMap.has(trimmed)) {
                node.nodeValue = raw.replace(trimmed, phraseMap.get(trimmed));
              }
            }
            return;
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node;
            if (!el.closest(NON_TRANSLATABLE_PARENT_SELECTOR)) {
              translateNodeTree(el, phraseMap);
            }
          }
        });
      });
    });

    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [i18nInstance.language]);

  return (
    <LanguageContext.Provider
      value={{
        language: normalizeLanguageCode(i18nInstance.language || "en"),
        changeLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

